import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import LocationFields from "@/components/LocationFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** One-time friendly nudge on the feed for users without city/state. Dismiss is remembered per user. */
export default function CompleteLocationBanner() {
  const { user, setUser } = useAuth();
  const key = `pdv-loc-banner-dismissed::${user?.user_id}`;
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem(key) === "1"; } catch { return false; } });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ country: user?.country || "", state: user?.state || "", city: user?.city || "", cep: user?.cep || "" });
  const [saving, setSaving] = useState(false);

  if (!user || dismissed || (user.city && user.state)) return null;

  const dismiss = () => { try { localStorage.setItem(key, "1"); } catch { /* ignore */ } setDismissed(true); };
  const save = async () => {
    if (!form.country.trim() || !form.state.trim() || !form.city.trim()) return toast.error("Preencha país, estado e cidade");
    setSaving(true);
    try {
      const r = await axios.put(`${API}/auth/profile`, { country: form.country.trim(), state: form.state.trim(), city: form.city.trim(), cep: form.cep || "", street: form.street || "", neighborhood: form.neighborhood || "" });
      setUser(r.data);
      toast.success("Localização salva! Obrigado 🎉");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    setSaving(false);
  };

  return (
    <Card className="p-4 border-primary/30 bg-gradient-to-br from-primary/5 to-orange-500/5" data-testid="complete-location-banner">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><MapPin className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">De onde você é, {(user.name || "").split(" ")[0]}?</div>
          <p className="text-xs text-muted-foreground">Informe sua cidade e estado para ver trabalhos, caronas e anúncios perto de você. Leva 10 segundos.</p>
          {!open ? (
            <div className="flex gap-2 mt-2">
              <Button size="sm" className="rounded-full" onClick={() => setOpen(true)} data-testid="complete-location-open">Preencher agora</Button>
              <Button size="sm" variant="ghost" className="rounded-full" onClick={dismiss} data-testid="complete-location-later">Depois</Button>
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              <LocationFields form={form} setForm={setForm} idPrefix="locbanner" />
              <div className="flex gap-2">
                <Button size="sm" className="rounded-full" onClick={save} disabled={saving} data-testid="complete-location-save">{saving ? "Salvando..." : "Salvar"}</Button>
                <Button size="sm" variant="ghost" className="rounded-full" onClick={dismiss} data-testid="complete-location-dismiss">Agora não</Button>
              </div>
            </div>
          )}
        </div>
        <button type="button" onClick={dismiss} className="text-muted-foreground hover:text-foreground" aria-label="Fechar" data-testid="complete-location-close"><X className="w-4 h-4" /></button>
      </div>
    </Card>
  );
}
